/**
 * The two writes in the external-DeCA surface that are not plain JSON calls.
 *
 * `PUT /external-decas/{id}/pdf` is multipart, and so is `POST /external-decas` when the copy comes with
 * the record (#184). The portal had exactly one precedent for that — `ImportScreen.upload()` — which goes
 * through the generated client with openapi-fetch's
 * `bodySerializer` rather than hand-rolling a `fetch`. That is worth keeping, because the client
 * is what carries the bearer token, the organisation context and the `Accept-Language` header;
 * a hand-rolled upload would silently drop all three.
 *
 * It lives here rather than in either screen because **both** of them attach: the list attaches
 * the optional copy right after creating the reference, and the detail attaches or replaces it
 * afterwards. Two copies of a `bodySerializer` and a `file as unknown as string` cast is how the
 * two would drift.
 *
 * Deliberately **not** a query hook: there is no `useMutation` in this portal (see
 * `AdminUsersScreen`), and this is called from inside the same `try/catch/finally` with local
 * `busy`/`error` state that every other write uses.
 */

import { unwrap, type components } from '@deca/api'

import { api } from './api.ts'

/**
 * A DeCA another party generated, recorded here to discharge this company's own one-year
 * retention duty (BOE Second.4). Aliased from the generated schema, never re-declared: the
 * client is generated from the frozen contract and CI fails on drift, so a hand-written twin
 * would be the one thing that could disagree with the API and still compile.
 */
export type ExternalDecaReference = components['schemas']['ExternalDecaReference']

/**
 * The ceiling the API enforces — the same 5 MB BOE Second.1 sets for a DeCA file.
 *
 * Checked in the browser as well, so a 40 MB scan is refused before it is uploaded rather than
 * after. The server's `EXTERNAL_DECA_TOO_LARGE` remains the authority; this only saves the wait.
 */
export const EXTERNAL_DECA_MAX_BYTES = 5 * 1024 * 1024

/**
 * Why the files handed over — chosen or dropped (#225) — are not taken, as a catalogue key; `null`
 * when they are one PDF the API will take.
 *
 * A PDF by its declared type or its name, as the file input's `accept` has it: a browser that
 * knows no type for a file sends it with none, which a mail program's attachment can be. The
 * server reads the bytes and stays the authority (`EXTERNAL_DECA_NOT_A_PDF`); this only refuses
 * what is plainly something else before it is sent.
 */
export function pdfRefusal(files: ArrayLike<File>): string | null {
  // Webmail's attachment arrives as a link, so a drop can carry no file at all.
  if (files.length === 0) return 'externalDecas.dropNoFile'
  // One record takes one copy: a second file is not dropped silently.
  if (files.length > 1) return 'externalDecas.dropSeveral'
  const file = files[0]!
  if (file.type !== 'application/pdf' && !/\.pdf$/i.test(file.name)) return 'externalDecas.dropNotPdf'
  if (file.size > EXTERNAL_DECA_MAX_BYTES) return 'errors.code.EXTERNAL_DECA_TOO_LARGE'
  return null
}

/** `184312` → `180` — the whole-kilobyte figure the archived-copy panel prints. */
export function kilobytes(bytes: number): number {
  return Math.max(1, Math.round(bytes / 1024))
}

export async function attachExternalDecaPdf(
  externalDecaId: string,
  file: File,
): Promise<ExternalDecaReference> {
  return unwrap(
    api.PUT('/api/v1/external-decas/{externalDecaId}/pdf', {
      params: { path: { externalDecaId } },
      // `format: binary` generates as `string`, so the File is cast here and handed to FormData
      // by the serializer below — the same confined cast `ImportScreen.upload()` makes.
      body: { file: file as unknown as string },
      bodySerializer(body: { file: string }) {
        const form = new FormData()
        form.append('file', body.file as unknown as File)
        return form
      },
    }),
  )
}

/**
 * Records a reference together with its copy, in one multipart `POST` (#184). The URL and the note go with
 * it only when given: a record made from its PDF alone has no URL.
 *
 * One request rather than create-then-attach: the server writes the record and its copy together, so a
 * failed upload leaves nothing behind, never a URL-only record behind an error. The serializer builds the
 * form from the values it closes over; `body` is there for openapi-fetch's types, with the same confined
 * `File` cast `attachExternalDecaPdf` makes.
 */
export async function createExternalDecaWithPdf(
  file: File,
  fields: { url: string | null; label: string | null },
): Promise<ExternalDecaReference> {
  const { url, label } = fields
  return unwrap(
    api.POST('/api/v1/external-decas', {
      body: { file: file as unknown as string, ...(url ? { url } : {}), ...(label ? { label } : {}) },
      bodySerializer() {
        const form = new FormData()
        form.append('file', file)
        if (url) form.append('url', url)
        if (label) form.append('label', label)
        return form
      },
    }),
  )
}
